import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sun, Moon, Cloud, CloudSun, CloudRain, CloudSnow, CloudLightning, CloudFog, CloudDrizzle, Wind, Droplets, MapPin, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/boards/data`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export function wmo(code, isDay = 1) {
  const c = Number(code);
  if (c === 0) return { label: 'Clear', Icon: isDay ? Sun : Moon, tone: 'text-amber-500' };
  if (c <= 2) return { label: c === 1 ? 'Mostly clear' : 'Partly cloudy', Icon: CloudSun, tone: 'text-amber-500' };
  if (c === 3) return { label: 'Overcast', Icon: Cloud, tone: 'text-slate-500' };
  if (c <= 48) return { label: 'Fog', Icon: CloudFog, tone: 'text-slate-500' };
  if (c <= 57) return { label: 'Drizzle', Icon: CloudDrizzle, tone: 'text-sky-600' };
  if (c <= 67) return { label: c >= 66 ? 'Freezing rain' : 'Rain', Icon: CloudRain, tone: 'text-sky-700' };
  if (c <= 77) return { label: 'Snow', Icon: CloudSnow, tone: 'text-sky-500' };
  if (c <= 82) return { label: 'Showers', Icon: CloudRain, tone: 'text-sky-700' };
  if (c <= 86) return { label: 'Snow showers', Icon: CloudSnow, tone: 'text-sky-500' };
  return { label: 'Thunderstorm', Icon: CloudLightning, tone: 'text-violet-600' };
}

const dayName = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-CA', { weekday: 'short' });
const hour = (iso) => new Date(iso).toLocaleTimeString('en-CA', { hour: 'numeric' }).replace(/\.m\./g, 'm').replace(' ', '');

export default function Weather({ config, size }) {
  const place = config?.place || 'St. George, Ontario';
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let on = true;
    const load = () => axios.get(`${API}/weather`, { ...auth(), params: { place } }).then(r => on && (setD(r.data), setErr(''))).catch(e => on && setErr(e.response?.data?.detail || 'Weather unavailable'));
    load();
    const t = setInterval(load, 10 * 60000);
    return () => { on = false; clearInterval(t); };
  }, [place]);

  if (err) return <p className="p-4 text-sm text-red-600" data-testid="weather-error">{err}</p>;
  if (!d) return <div className="p-4 text-sm text-slate-400 animate-pulse flex items-center gap-2"><RefreshCw className="h-3.5 w-3.5 animate-spin" /> Checking the sky over {place}…</div>;
  const cur = d.current || {};
  const W = wmo(cur.code, cur.is_day);
  const showHourly = size.h >= 5 && size.w >= 3;
  const showDaily = size.h >= 7 || (size.h >= 5 && size.w >= 5);
  const hazard = (cur.gust || 0) >= 50 || [65, 66, 67, 75, 82, 86, 95, 96, 99].includes(Number(cur.code));
  return (
    <div className="p-3 h-full flex flex-col text-slate-800" data-testid="weather-widget">
      <div className="flex items-center gap-1 text-[11px] text-slate-500 min-w-0"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{d.place}</span></div>
      <div className="flex items-center gap-3 mt-1">
        <W.Icon className={`h-12 w-12 ${W.tone}`} strokeWidth={1.5} />
        <div>
          <div className="text-4xl font-black leading-none">{Math.round(cur.temp)}°</div>
          <div className="text-xs text-slate-600 mt-1">{W.label} · feels {Math.round(cur.feels)}°</div>
        </div>
        <div className="ml-auto text-right text-[11px] text-slate-600 space-y-0.5">
          <div className="flex items-center justify-end gap-1"><Wind className="h-3 w-3" />{Math.round(cur.wind)} <span className="text-slate-400">g {Math.round(cur.gust)} km/h</span></div>
          <div className="flex items-center justify-end gap-1"><Droplets className="h-3 w-3" />{cur.rh}%</div>
          {d.sunrise && size.w >= 4 && <div className="text-slate-400">{hour(d.sunrise)} – {hour(d.sunset)}</div>}
        </div>
      </div>
      {hazard && <p className="mt-2 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="weather-hazard">Heads up for crews outside — {W.label.toLowerCase()}{cur.gust >= 50 ? `, gusts ${Math.round(cur.gust)} km/h` : ''}.</p>}
      {showHourly && (
        <div className="mt-3 flex gap-1 overflow-x-auto no-drag">
          {(d.hourly || []).slice(0, Math.max(6, size.w * 2)).map(h => { const H = wmo(h.code); return (
            <div key={h.t} className="flex flex-col items-center min-w-[42px] text-[10px] text-slate-600">
              <span>{hour(h.t)}</span>
              <H.Icon className={`h-4 w-4 my-0.5 ${H.tone}`} />
              <span className="font-bold text-slate-800">{Math.round(h.temp)}°</span>
              <span className={h.pop >= 40 ? 'text-sky-700 font-semibold' : 'text-slate-400'}>{h.pop}%</span>
            </div>); })}
        </div>
      )}
      {showDaily && (
        <div className="mt-auto pt-2 border-t border-slate-200 grid grid-cols-6 gap-1">
          {(d.daily || []).slice(0, 6).map((x, i) => { const D = wmo(x.code); return (
            <div key={x.d} className="text-center text-[10px] text-slate-600">
              <div className="font-semibold text-slate-700">{i === 0 ? 'Today' : dayName(x.d)}</div>
              <D.Icon className={`h-4 w-4 mx-auto my-0.5 ${D.tone}`} />
              <div><span className="font-bold text-slate-800">{Math.round(x.hi)}°</span> <span className="text-slate-400">{Math.round(x.lo)}°</span></div>
              <div className={x.pop >= 40 ? 'text-sky-700' : 'text-slate-400'}>{x.pop}%</div>
            </div>); })}
        </div>
      )}
    </div>
  );
}
